import type { ReactNode } from 'react';
import type { PaymentChainRow } from '../projects';
import { openPaymentStage, type PaymentAuthority } from '../payment-approval';
import { displayName, type Profiles } from '../people';
import { fmtMoney } from '../money';
import { MONTHS } from '../when';
import Icon from './Icon';

/*
 * THE PAYMENT-APPROVAL CHAIN, AS THE INVOICE DRAWER DRAWS IT — executive-approver
 * tier (surfaces 2a / 3a, approved 4 Oct 2026). Given the line's frozen (or
 * previewed) chain rows, it renders WHO a payment waits on and the chain
 * stage-by-stage: the work-acceptance that opened it, each authorisation stage
 * (done / open-now / waiting, with its quorum and any value band), the
 * separation-of-duties seats shown greyed with the reason, and the final
 * "payment authorised" node. Pure display — every action (authorise, remind,
 * send) stays in the drawer footer. Built on the app's own registers, no new
 * visual language.
 */

const lc = (s?: string) => (s ?? '').trim().toLowerCase();

/** A stored ms instant → "3 Oct" (no year — the chain is read at a glance). */
function fmtShort(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

type SeatState = 'ok' | 'rej' | 'open' | 'you' | 'wait' | 'block';
interface Seat {
  key: string;
  main: string;
  sub: string;
  when: string;
  whenKind?: 'next' | 'you';
  state: SeatState;
}
interface StageView {
  key: string;
  node: 'done' | 'open' | 'wait' | 'rej';
  no: number | null; // the stage number, or null for a ticked/locked node
  name: string;
  rule?: string;
  quorum?: string;
  openTag?: boolean;
  bandApplies?: number;
  seats: Seat[];
}

const SEAT_ICON: Record<SeatState, string> = {
  ok: 'check', rej: 'warning', open: 'clock', you: 'shield', wait: '', block: 'lock',
};

export interface ChainView {
  stages: StageView[];
  /** The open stage's label + who it waits on + counts, for the lead banner. */
  waitsOn: { stageName: string; names: string[]; signed: number; need: number } | null;
  /** The band that put a higher stage in the chain, if one applied. */
  band: { amount: number; stageName: string } | null;
}

/**
 * THE VIEW MODEL — turns the frozen rows into displayed stages. `acceptance`
 * prepends the work-accepted node; `stageLabel` names a stage by its id (read from
 * the current policy, display-only). `auth` is the viewer's authority on the open
 * stage, so their own overridable/next seat reads as "you".
 */
export function buildChainView(opts: {
  rows: PaymentChainRow[];
  stageLabel: (stepId: string) => string;
  acceptance?: { by?: string; at?: number };
  meEmail: string;
  acceptedBy?: string;
  auth: PaymentAuthority | null;
  profiles: Profiles;
  settled: boolean;
}): ChainView {
  const { rows, stageLabel, acceptance, meEmail, acceptedBy, auth, profiles, settled } = opts;
  const me = lc(meEmail);
  const accepter = lc(acceptedBy);
  const openStep = openPaymentStage(rows);

  // Group rows by stage, in frozen order.
  const order: string[] = [];
  const groups = new Map<string, PaymentChainRow[]>();
  for (const r of rows) {
    if (!groups.has(r.step)) { groups.set(r.step, []); order.push(r.step); }
    groups.get(r.step)!.push(r);
  }
  order.sort((a, b) => (groups.get(a)![0].stageSeq) - (groups.get(b)![0].stageSeq));

  const stages: StageView[] = [];
  let waitsOn: ChainView['waitsOn'] = null;
  let band: ChainView['band'] = null;

  // Work accepted — the node the authorisation chain opens from.
  if (acceptance?.at) {
    stages.push({
      key: 'accepted',
      node: 'done',
      no: null,
      name: 'Work accepted',
      seats: [{
        key: 'accepted-by',
        main: displayName(accepter, profiles),
        sub: '— work verified',
        when: fmtShort(acceptance.at),
        state: 'ok',
      }],
    });
  }

  order.forEach((step, i) => {
    const g = groups.get(step)!;
    const modeRow = g.find((r) => r.stageMode);
    const mode = modeRow?.stageMode ?? 'all';
    const need = mode === 'anyOne' ? 1 : typeof mode === 'object' ? Math.min(mode.quorum, g.length) : g.length;
    const signed = g.filter((r) => r.signedAt && r.answer === 'approved').length;
    const rejected = g.some((r) => r.signedAt && r.answer === 'rejected');
    const met = signed >= need;
    const isOpen = step === openStep;
    const bandAmt = g.find((r) => r.band != null)?.band;
    if (bandAmt != null && band == null) band = { amount: bandAmt, stageName: stageLabel(step) };

    const seats: Seat[] = g.map((r, j) => {
      const main = r.who.name || displayName(lc(r.who.email), profiles);
      const position = r.who.position ? `— ${r.who.position}` : '';
      const iAccepted = !!lc(r.who.email) && lc(r.who.email) === accepter;
      const isMe = !!lc(r.who.email) && lc(r.who.email) === me;
      if (r.signedAt && r.answer === 'approved') {
        return { key: `${step}-${j}`, main, sub: `${position}${r.sodOverride ? ' · control exception' : ''}`, when: fmtShort(r.signedAt), state: 'ok' };
      }
      if (r.signedAt && r.answer === 'rejected') {
        return { key: `${step}-${j}`, main, sub: `${position} · declined${r.reason ? ` — ${r.reason}` : ''}`, when: 'Declined', state: 'rej' };
      }
      // Unsigned.
      const overridableMine = isMe && !!auth?.sodOverride;
      if (iAccepted && !overridableMine) {
        return { key: `${step}-${j}`, main, sub: '— can’t authorise, accepted this work (separation of duties)', when: '', state: 'block' };
      }
      if (isOpen) {
        if (isMe && auth?.allowed) {
          return { key: `${step}-${j}`, main, sub: `${position} · that’s you`, when: auth.sodOverride ? 'Override' : 'Your turn', whenKind: 'you', state: 'you' };
        }
        return { key: `${step}-${j}`, main, sub: position, when: 'Next', whenKind: 'next', state: 'open' };
      }
      return { key: `${step}-${j}`, main, sub: position, when: '', state: 'wait' };
    });

    stages.push({
      key: step,
      node: met ? 'done' : rejected ? 'rej' : isOpen ? 'open' : 'wait',
      no: met || rejected ? null : i + 1,
      name: stageLabel(step),
      rule: mode === 'anyOne' ? 'any one' : need > 1 && !isOpen ? `${need} must sign` : undefined,
      quorum: isOpen && need > 1 ? `${need} must sign · ${signed} so far` : undefined,
      openTag: isOpen,
      bandApplies: bandAmt ?? undefined,
      seats,
    });

    if (isOpen) {
      const names = g
        .filter((r) => !r.signedAt && !(lc(r.who.email) && lc(r.who.email) === accepter))
        .map((r) => r.who.name || displayName(lc(r.who.email), profiles));
      waitsOn = { stageName: stageLabel(step), names, signed, need };
    }
  });

  // Payment authorised — the final node.
  stages.push({
    key: 'authorised',
    node: settled ? 'done' : 'wait',
    no: null,
    name: 'Payment authorised',
    seats: [{
      key: 'final',
      main: '',
      sub: settled
        ? 'Cleared every required stage — now in Ready to Pay for finance to settle.'
        : 'Unlocks when every stage above has signed, then this line moves to Ready to Pay.',
      when: '',
      state: settled ? 'ok' : 'wait',
    }],
  });

  return { stages, waitsOn, band };
}

function CoinMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="8" /><path d="M12 8v8M9.5 10h3.5a1.5 1.5 0 0 1 0 3H10a1.5 1.5 0 0 0 0 3h3.5" />
    </svg>
  );
}

/** The chain, drawn. `lead` is an optional banner slotted above the stages (the
 *  "waiting on X" note, or the "you're asked to sign" cue the drawer supplies). */
export default function PaymentChainPanel({ view, lead, lineBase }: {
  view: ChainView;
  lead?: ReactNode;
  lineBase: number;
}) {
  return (
    <>
      <div className="id-sec">
        {lead}
        <div className="id-h" style={{ marginTop: lead ? 12 : undefined }}>Authorisation chain</div>
        <div className="pa-chain">
        {view.stages.map((st) => (
          <div className="pa-stage" key={st.key}>
            <div className="pa-stage-head">
              <span className={`pa-no ${st.node === 'done' ? 'done' : st.node === 'open' ? 'open' : 'wait'}`}>
                {st.node === 'done' ? <Icon name="check" size={11} /> : st.node === 'wait' && st.no == null ? <Icon name="lock" size={11} /> : st.no}
              </span>
              <span className="pa-name" style={st.no == null && st.node === 'wait' ? { color: 'var(--t3)' } : undefined}>{st.name}</span>
              {st.rule && <span className="pa-rule">{st.rule}</span>}
              {st.quorum && <span className="pa-quorum">{st.quorum}</span>}
              {st.openTag && <span className="pa-open-tag">Open now</span>}
            </div>
            {st.bandApplies != null && (
              <div className="pa-stage-band">
                <span className="pa-band-mini"><CoinMark /> Above {fmtMoney(st.bandApplies)} — applies</span>
              </div>
            )}
            <ul className="pa-signers">
              {st.seats.map((s) => (
                <li className={`pa-sig ${s.state === 'block' || s.state === 'wait' ? 'is-block' : ''}`} key={s.key}>
                  <span className={`pa-sig-ic ${s.state}`}>
                    {SEAT_ICON[s.state] && <Icon name={SEAT_ICON[s.state] as 'check'} size={11} />}
                  </span>
                  <span>
                    {s.main && <span className="pa-sig-main">{s.main}</span>}{' '}
                    <span className="pa-sig-sub">{s.sub}</span>
                  </span>
                  {s.when && <span className={`pa-sig-when ${s.whenKind === 'next' ? 'is-next' : s.whenKind === 'you' ? 'is-you' : ''}`}>{s.when}</span>}
                </li>
              ))}
            </ul>
          </div>
        ))}
        </div>
      </div>
      {view.band && (
        <div className="id-sec">
          <div className="id-h">The band that applied</div>
          <div className="guard">
            <CoinMark />
            <span>
              This invoice is <b>{fmtMoney(lineBase)}</b>, at or above the <b>{fmtMoney(view.band.amount)}</b>{' '}
              <b>{view.band.stageName}</b> band (compared in base currency), so that stage was added above the
              earlier one. Below it, only the earlier stage would be asked.
            </span>
          </div>
        </div>
      )}
    </>
  );
}
